import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function AsignarTrabajadorForm() {
  const { id } = useParams(); // ID de la orden de servicio
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orden, setOrden] = useState({});
  const [detalles, setDetalles] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [asignacion, setAsignacion] = useState({
    id_trabajador: '',
    precio_servicio: '',
    porcentaje_comision: 10,
    comision: '',
  });

  // Cargar datos iniciales
  useEffect(() => {
    fetchOrden();
    fetchDetalles();
    fetchTrabajadores();
  }, []);

  const fetchOrden = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/ordenes/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setOrden(response.data);
    } catch (error) {
      console.error('Error al obtener la orden:', error);
    }
  };

  const fetchDetalles = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/detalle-ordenes/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setDetalles(response.data);
      calcularPrecioTotal(response.data);
    } catch (error) {
      console.error('Error al obtener los detalles:', error);
    }
  };

  const fetchTrabajadores = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setTrabajadores(response.data);
    } catch (error) {
      console.error('Error al obtener trabajadores:', error);
    }
  };

  // Calcular el precio total del servicio
  const calcularPrecioTotal = (detalles) => {
    const total = detalles.reduce((sum, item) => sum + item.precio * item.cantidad, 0);
    setAsignacion((prev) => ({ ...prev, precio_servicio: total, comision: (total * prev.porcentaje_comision) / 100 }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAsignacion((prev) => ({
      ...prev,
      [name]: value,
      comision: name === 'porcentaje_comision' ? (prev.precio_servicio * value) / 100 : prev.comision,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${config.API_BASE_URL}/api/ordenes/${id}/asignar-trabajador`, asignacion, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      navigate(`/home/ordenes/editar/${id}`); // Redirige a la orden después de asignar
    } catch (error) {
      console.error('Error al asignar trabajador:', error);
    }
  };

  const handleCancel = () => {
    navigate(`/home/ordenes/editar/${id}`);
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          border: '1px solid #ccc',
          padding: 2,
          marginBottom: 3,
          borderRadius: 1,
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography variant="subtitle1">
          <strong>Orden de Trabajo:</strong> {orden.id_orden_servicio || '000000'}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Estado:</strong> {orden.estado || 'Pendiente'}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Local:</strong> {orden.local || ''}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Cliente:</strong> {orden.cliente || ''}
        </Typography>
        <Typography variant="subtitle1">
          <strong>Fecha:</strong> {orden.fecha_orden || ''}
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ marginBottom: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Servicio</TableCell>
              <TableCell>Precio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalles.map((detalle, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{detalle.nombre_servicio}</TableCell>
                <TableCell>{detalle.precio}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2} align="right">
                <strong>Total</strong>
              </TableCell>
              <TableCell>{asignacion.precio_servicio}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Trabajador</InputLabel>
          <Select
            name="id_trabajador"
            value={asignacion.id_trabajador}
            onChange={handleChange}
            required
          >
            {trabajadores.map((trabajador) => (
              <MenuItem key={trabajador.id_trabajador} value={trabajador.id_trabajador}>
                {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Precio Base"
          type="number"
          fullWidth
          margin="normal"
          name="precio_servicio"
          value={asignacion.precio_servicio}
          disabled
        />
        <TextField
          label="% Comisión"
          type="number"
          fullWidth
          margin="normal"
          name="porcentaje_comision"
          value={asignacion.porcentaje_comision}
          onChange={handleChange}
          required
        />
        <TextField
          label="Comisión"
          type="number"
          fullWidth
          margin="normal"
          name="comision"
          value={asignacion.comision}
          disabled
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          sx={{ marginTop: 2 }}
        >
          Asignar Trabajador
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          sx={{ marginTop: 2 }}
          onClick={handleCancel}
        >
          Regresar
        </Button>
      </form>
    </Container>
  );
}

export default AsignarTrabajadorForm;
